import { zodResolver } from "@hookform/resolvers/zod";
import { FunctionalTypeFormSelect } from "features/entities/FunctionalTypeFormSelect";
import { FUND_ENTITY_FUNCTIONAL_TYPES } from "features/entities/FundEntityConstants";
import { FundFamilyFormSelect } from "features/entities/FundFamilyFormSelect";
import { LegalTypeFormSelect } from "features/entities/LegalTypeFormSelect";
import { useGlobalEntityUpdate } from "gen/endpoints/global-entity/global-entity";
import { FundEntityData } from "gen/model/fundEntityData";
import { GlobalEntityData } from "gen/model/globalEntityData";
import { globalEntityUpdateBody } from "gen/schemas/global-entity/global-entity";
import React from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { FormJurisdictionSelect } from "shared/components/input/select/JurisdictionSelect";
import { TaxIdTypeFormSelect } from "shared/components/input/select/TaxIdTypeSelect";
import { FormPatternInput } from "shared/components/input/text/PatternInput";
import { FormTextInput } from "shared/components/input/text/TextInput";

export type EntityProfileSection = "basic" | "tax";

export interface EntityEditFormProps {
  entity: GlobalEntityData | FundEntityData;
  onCloseComplete: () => void;
  section: EntityProfileSection;
}

export const EntityEditDrawer: React.FC<EntityEditFormProps> = ({
  entity,
  onCloseComplete,
  section,
}) => {
  const { t } = useTranslation(["common", "entity"]);

  const form = useForm<GlobalEntityData>({
    resolver: zodResolver(globalEntityUpdateBody),
    defaultValues: entity,
  });

  const { mutateAsync: editEntity } = useGlobalEntityUpdate();

  return (
    <FormDrawer
      title={t("entity:edit.title")}
      formProps={{
        form,
        onSubmit: async (data) => await editEntity({ id: entity.id!, data }),
        successMessage: t("entity:edit.success"),
        objectDisplayName: entity.legal_name,
      }}
      onCloseComplete={onCloseComplete}
      bodyProps={{
        "data-private": true,
      }}
    >
      {section === "basic" && <EntityEditBasicFields />}
      {section === "tax" && <EntityEditTaxFields />}
    </FormDrawer>
  );
};

const EntityEditBasicFields: React.FC = () => {
  const { t } = useTranslation(["common", "entity"]);

  const { control } = useFormContext();
  const functionalType = useWatch({
    control,
    name: "functional_type",
  });

  const isFundEntity = FUND_ENTITY_FUNCTIONAL_TYPES.some(
    (type) => type === functionalType,
  );
  const isInvestorEntity = functionalType === "investor";

  return (
    <>
      <FunctionalTypeFormSelect
        isFundFamilyRelated={isFundEntity}
        isInvestorEntity={isInvestorEntity}
      />
      {isFundEntity && <FundFamilyFormSelect />}
      <FormJurisdictionSelect />
      <LegalTypeFormSelect />
      <FormTextInput name="legal_name" title={t("entity:fullEntityName")} />
      <FormTextInput name="external_id" title={t("entity:externalId")} />
    </>
  );
};

const EntityEditTaxFields = () => {
  const { t } = useTranslation(["common"]);
  return (
    <>
      <TaxIdTypeFormSelect />
      <FormPatternInput
        name="tax_id"
        title={t("common:tax.id")}
        format="###-##-####"
      />
    </>
  );
};
